import { QuickActionsButtonGroupLabelsMenu } from '@aibees/elephant/components/quick-actions-button-group-labels-menu';
import { useTableActionContext } from '@aibees/elephant/context';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { useHandleFindButton } from '@aibees/elephant/hooks/use-handle-find-button';
import { SearchType } from '@aibees/elephant/types';
import { useFullScreenLoadingContext } from '@aibees/shared/context';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { memo, useState } from 'react';
import { ExportDialog } from '../export-dialog';
import { QuickActionMenuItem } from '../quick-action-menu-item';
import { QuickActionsButtonGroupListsMenu } from '../quick-actions-button-group-lists-menu';
import { AssignOwnerDialog } from '../search-table-action-dialog-more/components/search-table-action-assign-owner-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanyQuickActionsProps as Props } from './types';

export const CompanyQuickActions = memo(({ className, handleClose }: Props) => {
  const { item } = useActionButtonContext();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { ownerDialogState, exportDialogState, setIntegrationConfig } =
    useTableActionContext();
  const labelsMenuOpen = useBoolean();
  const listsMenuOpen = useBoolean();
  const { createSearchListAndRedirect } = useHandleFindButton({
    type: SearchType.Company,
  });
  const { setTitle: setLoadingScreenTitle, state: loadingScreenState } =
    useFullScreenLoadingContext();

  const handleClickFindPeople = () => {
    if (item?.id) {
      loadingScreenState.onTrue();
      setLoadingScreenTitle('Redirecting...');
      createSearchListAndRedirect([item?.id]);
    }
  };

  function handleClickExport() {
    exportDialogState.onTrue();
    handleClose?.();
    setIntegrationConfig(undefined);
  }

  function handleClickAssignOwner() {
    ownerDialogState.onTrue();
    handleClose?.();
  }

  return (
    <Styled.Wrapper
      className={classnames('CompanyQuickActions', className)}
      sx={SX.root}
    >
      <QuickActionMenuItem
        title="Export"
        icon="export"
        onClick={handleClickExport}
      />

      <QuickActionMenuItem
        title="Find People"
        icon="people"
        onClick={handleClickFindPeople}
      />
      <QuickActionMenuItem
        title="Lists"
        icon="list"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          listsMenuOpen.onTrue();
        }}
        hasChildren
        childState={listsMenuOpen}
      />
      <QuickActionMenuItem
        title="Labels"
        icon="label"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          labelsMenuOpen.onTrue();
        }}
        hasChildren
        childState={labelsMenuOpen}
      />
      <QuickActionMenuItem
        title="Assign Owner"
        icon="account-tag"
        onClick={handleClickAssignOwner}
      />

      <QuickActionsButtonGroupListsMenu
        state={listsMenuOpen}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />
      <QuickActionsButtonGroupLabelsMenu
        state={labelsMenuOpen}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />

      <ExportDialog key="export" formId="company-quick-actions-export-dialog" />
      <AssignOwnerDialog />
    </Styled.Wrapper>
  );
});

CompanyQuickActions.displayName = 'CompanyQuickActions';
